import { GAP_MAX_UNKNOWN_DAYS, GAP_TOLERANCE } from './constants';
import type { Day } from './day';
import { withinDepthTolerance } from './depth';

/** One SKU's discount evidence at one vendor channel; indices are into the day axis. */
export type EvidenceRun = {
  readonly ean: string;
  readonly vendorKey: string;
  /** First discount day. */
  readonly first: number;
  /** Last discount day. */
  readonly last: number;
  readonly discountDays: readonly number[];
  /** Mean Day.depth over the discount days within DEPTH_TOLERANCE of their median. */
  readonly depth: number;
  /** Mean Day.regularPrice over the discount days. */
  readonly regularPrice: number;
};

// Index bounds of a day range, both inclusive.
type DayInterval = { readonly from: number; readonly to: number };

function meanOver(
  days: readonly Day[],
  indices: readonly number[],
  value: (d: Day) => number | null
): number {
  let sum = 0;
  for (const i of indices) sum += value(days[i]) ?? 0;
  return sum / indices.length;
}

// A single deviating day does not move the tier's depth (CONTEXT.md, Campaign run).
function tierDepth(days: readonly Day[], discountDays: readonly number[]): number {
  const depths = discountDays.map((i) => days[i].depth ?? 0).sort((a, b) => a - b);
  const mid = depths.length >> 1;
  const median = depths.length % 2 === 1 ? depths[mid] : (depths[mid - 1] + depths[mid]) / 2;
  const conforming = depths.filter((depth) => withinDepthTolerance(depth, median));
  // An even count's median can sit between two values both out of tolerance; then no day conforms.
  const counted = conforming.length > 0 ? conforming : depths;
  return counted.reduce((a, b) => a + b, 0) / counted.length;
}

function evidenceRun(
  days: readonly Day[],
  ean: string,
  vendorKey: string,
  discountDays: readonly number[]
): EvidenceRun {
  return {
    ean,
    vendorKey,
    first: discountDays[0],
    last: discountDays[discountDays.length - 1],
    discountDays,
    depth: tierDepth(days, discountDays),
    regularPrice: meanOver(days, discountDays, (d) => d.regularPrice),
  };
}

/** The run's discount days within `from` to `to` inclusive as a run of their own, or null when there are none. */
export function sliceRun(
  days: readonly Day[],
  run: EvidenceRun,
  from: number,
  to: number
): EvidenceRun | null {
  const discountDays = run.discountDays.filter((i) => i >= from && i <= to);
  return discountDays.length > 0 ? evidenceRun(days, run.ean, run.vendorKey, discountDays) : null;
}

/** Runs of discount days, bridging up to GAP_TOLERANCE regular days and closed by any non-observed day (spec decision 8). */
export function segmentRuns(days: readonly Day[], ean: string, vendorKey: string): EvidenceRun[] {
  const runs: EvidenceRun[] = [];
  let discountDays: number[] = [];
  let regularSinceDiscount = 0;
  const close = () => {
    for (const tier of splitByDepthTier(days, discountDays)) {
      runs.push(evidenceRun(days, ean, vendorKey, tier));
    }
    discountDays = [];
    regularSinceDiscount = 0;
  };
  for (let i = 0; i < days.length; i++) {
    const { kind } = days[i];
    if (kind === 'discount') {
      discountDays.push(i);
      regularSinceDiscount = 0;
    } else if (
      discountDays.length > 0 &&
      (kind !== 'regular' || ++regularSinceDiscount > GAP_TOLERANCE)
    ) {
      close();
    }
  }
  close();
  return runs;
}

// A step in depth that holds for two or more discount days starts a new tier; a single deviating day stays (CONTEXT.md, Campaign run).
function splitByDepthTier(days: readonly Day[], discountDays: readonly number[]): number[][] {
  const tiers: number[][] = [];
  let tier: number[] = [];
  // Mean depth of the tier's conforming days; a kept outlier does not move it.
  let sum = 0;
  let count = 0;
  const depthOf = (i: number) => days[i].depth ?? 0;
  for (let n = 0; n < discountDays.length; n++) {
    const depth = depthOf(discountDays[n]);
    const next = n + 1 < discountDays.length ? depthOf(discountDays[n + 1]) : null;
    const current = count > 0 ? sum / count : depth;
    const steps =
      count > 0 &&
      !withinDepthTolerance(depth, current) &&
      next !== null &&
      !withinDepthTolerance(next, current) &&
      Math.sign(next - current) === Math.sign(depth - current);
    if (steps && next !== null) {
      // The two days that evidence the step seed the new tier together; a lone day before them cannot evidence a tier and goes with them.
      if (tier.length > 1) tiers.push(tier);
      tier = [...(tier.length > 1 ? [] : tier), discountDays[n], discountDays[n + 1]];
      sum = depth + next;
      count = 2;
      n++;
      continue;
    }
    tier.push(discountDays[n]);
    if (count === 0 || withinDepthTolerance(depth, sum / count)) {
      sum += depth;
      count++;
    }
  }
  if (tier.length > 0) tiers.push(tier);
  return tiers;
}

// Index of the nth regular day before `index`, or -1.
function regularBefore(days: readonly Day[], index: number, nth: number): number {
  let seen = 0;
  for (let i = index - 1; i >= 0; i--) {
    if (days[i].kind === 'regular' && ++seen === nth) return i;
  }
  return -1;
}

// Index of the nth regular day after `index`, or the axis length.
function regularAfter(days: readonly Day[], index: number, nth: number): number {
  let seen = 0;
  for (let i = index + 1; i < days.length; i++) {
    if (days[i].kind === 'regular' && ++seen === nth) return i;
  }
  return days.length;
}

function intervalWithin(days: readonly Day[], run: EvidenceRun, nth: number): DayInterval {
  return {
    from: regularBefore(days, run.first, nth) + 1,
    to: regularAfter(days, run.last, nth) - 1,
  };
}

/** The days the run could span: up to the nearest observed regular day on each side, exclusive (the snap bound). */
export function possibleInterval(days: readonly Day[], run: EvidenceRun): DayInterval {
  return intervalWithin(days, run, 1);
}

/** The days a wave may claim the run over: up to the third observed regular day on each side, exclusive (spec decision 11). */
export function membershipInterval(days: readonly Day[], run: EvidenceRun): DayInterval {
  return intervalWithin(days, run, GAP_TOLERANCE + 1);
}

/** True when `a` ends before `b` starts with at most GAP_TOLERANCE observed regular days strictly between. */
export function canJoin(days: readonly Day[], a: EvidenceRun, b: EvidenceRun): boolean {
  if (a.last >= b.first || !withinDepthTolerance(a.depth, b.depth)) return false;
  let regular = 0;
  for (let i = a.last + 1; i < b.first; i++) {
    if (days[i].kind === 'regular') regular++;
  }
  return regular <= GAP_TOLERANCE;
}

/** True when a barrier day lies strictly between `a` and `b`. */
export function crossesBarrier(
  barriers: ReadonlySet<number>,
  a: EvidenceRun,
  b: EvidenceRun
): boolean {
  return [...barriers].some((i) => i > a.last && i < b.first);
}

/** Joins consecutive runs of one series at most GAP_MAX_UNKNOWN_DAYS apart that canJoin and no barrier day separates (spec decisions 12 and 18). */
export function joinNearbyRuns(
  days: readonly Day[],
  runs: readonly EvidenceRun[],
  barriers: ReadonlySet<number> = new Set()
): EvidenceRun[] {
  const joined: EvidenceRun[] = [];
  for (const run of [...runs].sort((a, b) => a.first - b.first)) {
    const prev = joined[joined.length - 1];
    if (
      prev &&
      run.first - prev.last - 1 <= GAP_MAX_UNKNOWN_DAYS &&
      canJoin(days, prev, run) &&
      !crossesBarrier(barriers, prev, run)
    ) {
      joined[joined.length - 1] = evidenceRun(days, prev.ean, prev.vendorKey, [
        ...prev.discountDays,
        ...run.discountDays,
      ]);
    } else {
      joined.push(run);
    }
  }
  return joined;
}
